import { useUser } from '@myosh/myosh-login';
import { OdinIcon, Popover, PopoverContent, PopoverTrigger } from '@myosh/odin-components';
import { memo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { DIAGRAM_MODE } from '../../helpers/constants';
import { cn, makeDataCy } from '../../helpers/util';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectDiagramMode, setDiagramMode as toggleDiagram } from '../../redux/slices/diagram';
import AboutModal from './about-modal';

interface SettingsProps {
  showOnlyAboutOption?: boolean;
}

/**
 * Settings component that provides a menu for toggling diagram modes and displaying an About modal.
 *
 * @component
 * @example
 * return (
 *   <Settings />
 * )
 *
 * @returns {JSX.Element} The rendered Settings component.
 */
const Settings = ({ showOnlyAboutOption = false }: SettingsProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const diagramMode = useAppSelector(selectDiagramMode);
  const navigate = useNavigate();
  const location = useLocation();

  const { state } = useUser();
  const isSuperUser = state.user?.superuser === 'true';

  const defaultItems = [
    {
      name: 'Bowtie',
      action: () => setDiagramMode(DIAGRAM_MODE.BOWTIE),
      icon: 'Information',
      className: diagramMode === DIAGRAM_MODE.BOWTIE ? 'bg-primary-5' : '',
      visible: !showOnlyAboutOption,
    },
    {
      name: 'Butterfly',
      action: () => setDiagramMode(DIAGRAM_MODE.BUTTERFLY),
      icon: 'MenuFold',
      className: diagramMode === DIAGRAM_MODE.BUTTERFLY ? 'bg-primary-5' : '',
      visible: !showOnlyAboutOption,
    },
    {
      name: 'About',
      action: () => {
        setIsAboutModalOpen(true);
        setIsMenuOpen(false);
      },
    },
    {
      name: 'Configuration',
      action: () => {
        navigate('/configuration', { state: { from: `${location.pathname}${location.search}` } });
      },
      visible: isSuperUser && !showOnlyAboutOption,
    },
  ];

  const setDiagramMode = (selectedDiagramMode: DIAGRAM_MODE) => {
    setIsMenuOpen(false);
    dispatch(toggleDiagram(selectedDiagramMode));
  };

  return (
    <>
      <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger>
          <OdinIcon icon="Menu" className="text-primary-2" />
        </PopoverTrigger>
        <PopoverContent data-cy="settings-toggle-button" align="start" className="max-w-fit min-w-[200px] p-0">
          <ul>
            {defaultItems
              .filter((item) => item.visible !== false)
              .map((item) => {
                const dataCyName = makeDataCy(item.name);
                return (
                  <li
                    data-cy={`settings-item-${dataCyName}`}
                    key={item.name}
                    className={cn(
                      'hover:bg-primary-2 hover:text-mono-1 px-3 py-2 text-base hover:cursor-pointer',
                      item.className
                    )}
                    onClick={item.action}
                  >
                    {item.name}
                  </li>
                );
              })}
          </ul>
        </PopoverContent>
      </Popover>
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
    </>
  );
};

export default memo(Settings);
